import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import styled from 'styled-components';
import qs from 'qs';
import moment from 'moment';
import TVLayout from '../../layouts/TVLayout';
import Widget from './components/Widget';
import MaximizedPopup from './components/MaximizedPopup';
import {getIngests, getTransfers, getProxies, getFailedActions, getCsPending, getWorkOrders} from './actions';
import {CYCLE_DURATION} from './constants';

const formatDatetime = (value) => {
  if (!value) return '';
  let date;
  if (typeof value === 'number') {
    // Unix timestamp (seconds)
    date = new Date(value * 1000);
  } else {
    // String date (ISO or other format)
    date = new Date(value);
  }
  if (isNaN(date.getTime())) return value;

  // Check if date is today
  const now = new Date();
  const isToday = date.getDate() === now.getDate() &&
                  date.getMonth() === now.getMonth() &&
                  date.getFullYear() === now.getFullYear();

  const isCurrentYear = date.getFullYear() === now.getFullYear();

  if (isToday) {
    // Only show time for today
    return date.toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } else if (isCurrentYear) {
    // Show date without year for current year
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } else {
    // Show date with year for other years
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }
};

const renderStepNumber = (value, row) => {
  const stepNum = row.step_num_current && row.step_num_total ? `${row.step_num_current}/${row.step_num_total}` : '';
  return stepNum;
}

const renderStepDetails = (value, row) => {
  if (!row.step_type && !row.step_details) return '';
  const details = [row.step_type, row.step_details].filter(Boolean).join(': ');
  return details;
};

const renderStepAssignee = (value, row) => {
  if (!row.step_assignee) return '';
  const assigneeStyle = row.step_in_progress === 1 ? {color: '#5cb85c'} : {};
  return React.createElement('span', null, React.createElement('span', {style: assigneeStyle}, row.step_assignee));
};

const formatDueDate = (value, row) => {
  if (!value) return '';
  const date = typeof value === 'number' ? moment.unix(value) : moment(value);
  if (!date.isValid()) return value;

  const today = moment().startOf('day');
  const target = date.clone().startOf('day');
  const diffDays = target.diff(today, 'days');
  const time = date.format('h:mm A');
  const dueDateStr = date.format('MM/DD/YYYY hh:mm a');

  let text;
  if (diffDays === 0) text = `Today ${time}`;
  else if (diffDays === 1) text = `Tomorrow ${time}`;
  else if (diffDays === -1) text = `Yesterday ${time}`;
  else if (diffDays > 1) text = `In ${diffDays} days, ${time}`;
  else text = `${Math.abs(diffDays)} days ago, ${time}`;

  if (row && row.completion_vs_due_hours != null) {
    const hrs = row.completion_vs_due_hours;
    const style = hrs < 0 ? {color: '#dc3545'} : {};
    return React.createElement('span', null, text, ' ', React.createElement('span', {style}, `(${hrs})`));
  }

  return React.createElement('span', {title: 'Due date: ' + dueDateStr}, text);
};

const renderHoursLeft = (value) => {
  if (0 === value) return 0;
  if (value) {
    return `${value}h`;
  }
  return '-';
}

const renderHoursUntilDue = (value) => {
  if (value == null) return value;
  const abs = Math.abs(value);
  if (abs > 24) {
    const days = value / 24;
    return Math.abs(days) >= 5 ? `${Math.round(days)}d` : `${days.toFixed(1)}d`;
  }
  return `${value}h`;
}

// Static widget column configurations - defined once to avoid recreating on every render
const WIDGET_COLUMNS = {
  ingests: [
    {key: 'wo_memfis', label: 'WO Memfis', width: 20},
    {key: 'created_on', label: 'Created On', render: formatDatetime},
    {key: 'action_status', label: 'Ingest Status'},
    {key: 'ingest_status', label: 'WO Status'},
    {key: 'office', label: 'Office', width: 12}
  ],
  transfers: [
    {key: 'wo_memfis', label: 'WO Memfis'},
    {key: 'transfer_from', label: 'From'},
    {key: 'transfer_to', label: 'To'},
    {key: 'progress', label: 'Progress'},
    {key: 'transfer_status', label: 'Status'},
    {key: 'created_on', label: 'Started On', render: formatDatetime},
    {key: 'finished_on', label: 'Finished On', render: formatDatetime},
    {key: 'transfer_type', label: 'Type'}
  ],
  proxies: [
    {key: 'wo_memfis', label: 'WO Memfis'},
    {key: 'qod_proxy_status', label: 'QOD Status'},
    {key: 'mam_action_status', label: 'Render Status'},
    {key: 'created_on', label: 'Started On', render: formatDatetime},
    {key: 'uploaded_on', label: 'Finished On', render: formatDatetime}
  ],
  failedActions: [
    {key: 'action_name', label: 'Action Name'},
    {key: 'wo_memfis', label: 'WO Memfis', width: 15},
    {key: 'action_status', label: 'Status'},
    {key: 'problem_label', label: 'Problem'},
    {key: 'created_on', label: 'Created On', render: formatDatetime}
  ],
  csPending: [
    {key: 'wo_memfis', label: 'WO Memfis', width: 15},
    {key: 'title', label: 'Title'},
    {key: 'stage', label: 'Stage', maxWidth: 60},
    {key: 'hours_until_due', label: 'Due In', title: 'Hours until due', render: renderHoursUntilDue, width: 10, maxWidth: 90},
    {key: 'cs_user_name', label: 'CS User', width: 15}
  ],
  workOrders: [
    {key: 'wo_memfis', label: 'WO Memfis', width: 8, maxWidth: 100},
    {key: 'studio', label: 'Studio', width: 10, maxWidth: 100},
    {key: 'department', label: 'Dept', width: 10, maxWidth: 105},
    {key: 'location', label: 'Office', width: 10, maxWidth: 75},
    {key: 'title', label: 'Title'},
    {key: 'wo_type', label: 'Type', width: 5, maxWidth: 100},
    {key: 'qc_status', label: 'Status', width: 10, maxWidth: 200},
    {key: 'due_date', label: 'Due Date', render: formatDueDate, width: 10, maxWidth: 170},
    {key: 'hours_left', label: 'Remaining', title: 'Hours left', render: renderHoursLeft, width: 5, maxWidth: 90},
    {key: 'hours_until_due', label: 'Due In', title: 'Hours until due', render: renderHoursUntilDue, width: 5, maxWidth: 70},
    {key: 'step_num', label: 'Step#', render: renderStepNumber, width: 5, maxWidth: 75},
    {key: 'step_description', label: 'Step Description', render: renderStepDetails},
    {key: 'step_assignee', label: 'Assignee', render: renderStepAssignee, width: 10, maxWidth: 150}
  ]
};

const DashboardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #343a40;
  color: #fff;
  padding: 12px 24px;
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 2.1rem;
  font-weight: bold;
`;

const HeaderCenter = styled.div`
  text-align: center;

  .datetime {
    font-size: 1.2rem;
    font-weight: bold;
  }

  .updated {
    font-size: 1.1rem;
    opacity: 0.8;
  }

  .hours-info {
    font-size: 1.1rem;
    opacity: 0.8;
    margin-top: 4px;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
`;

const HealthBadge = styled.div`
  background: ${props => props.$color || '#28a745'};
  color: #fff;
  font-size: 1.6rem;
  font-weight: bold;
  padding: 5px 20px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: background-color 0.3s ease;
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: 1fr 1fr;
  gap: 16px;
  flex: 1;
  min-height: 0;
  padding: 14px;
  background: #e9ecef;

  ${props => props.$showProxies ? `
    .widget:nth-child(4) {
      grid-column: 1 / 2;
    }

    .widget:nth-child(5) {
      grid-column: 2 / 4;
    }
  ` : `
    .widget:nth-child(4) {
      grid-column: 1 / 4;
    }
  `}
`;

// Isolated Clock component - updates independently without causing parent re-renders
class Clock extends React.PureComponent {
  state = {
    currentTime: new Date()
  };

  componentDidMount() {
    this.clockInterval = setInterval(() => {
      this.setState({currentTime: new Date()});
    }, 1000);
  }

  componentWillUnmount() {
    if (this.clockInterval) {
      clearInterval(this.clockInterval);
    }
  }

  formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  render() {
    const {currentTime} = this.state;
    const {lastUpdated, hours} = this.props;

    return (
      <HeaderCenter>
        <div className="datetime">{this.formatDate(currentTime)} {this.formatTime(currentTime)}</div>
        {lastUpdated && (
          <div className="updated">Data from last {hours} hours; Last updated: {this.formatTime(lastUpdated)}</div>
        )}
      </HeaderCenter>
    );
  }
}

Clock.propTypes = {
  lastUpdated: PropTypes.instanceOf(Date),
  hours: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

class StatusDashboard extends React.Component {
  state = {
    lastUpdated: null,
    maximizedWidgetKey: null,
    snapshotData: {}
  };

  _woDataCache = {input: null, showComplete: null, output: []};

  componentDidMount() {
    this.fetchAllData();
    this.refreshInterval = setInterval(this.fetchAllData, CYCLE_DURATION);
  }

  componentWillUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  getHoursParam = () => {
    const {location} = this.props;
    const params = qs.parse(location.search, {ignoreQueryPrefix: true});
    return params.hours || null;
  };

  shouldDisplayProxies = () => {
    const {location} = this.props;
    const params = qs.parse(location.search, {ignoreQueryPrefix: true});
    return String(params.displayProxies || '') === '1';
  };

  shouldShowCompleteWO = () => {
    const {location} = this.props;
    const params = qs.parse(location.search, {ignoreQueryPrefix: true});
    return String(params.showCompleteWO || '') === '1';
  };

  shouldShowCalculatedStatus = () => {
    const {location} = this.props;
    const params = qs.parse(location.search, {ignoreQueryPrefix: true});
    return String(params.showStatus || '') === '1';
  };

  shouldShowNonDashWO = () => {
    const {location} = this.props;
    const params = qs.parse(location.search, {ignoreQueryPrefix: true});
    return String(params.showNonDashWO || '') === '1';
  };

  shouldShowCsPending = () => {
    const {location} = this.props;
    const params = qs.parse(location.search, {ignoreQueryPrefix: true});
    return String(params.cs || '') === '1';
  };

  getWidgetMetadata = () => {
    const showCs = this.shouldShowCsPending();
    return [
      {key: 'ingests', title: 'Ingests', pathField: 'file_path', pathField2: 'wo_full_title'},
      {key: 'transfers', title: 'Transfers', pathField: 'file_path'},
      {key: 'proxies', title: 'Proxies', pathField: 'proxy_file_name'},
      showCs
        ? {key: 'csPending', title: 'CS Pending'}
        : {key: 'failedActions', title: 'Failed Actions'},
      {key: 'workOrders', title: 'Work Orders'}
    ];
  };

  fetchAllData = () => {
    const {dispatch} = this.props;
    const hours = this.getHoursParam();
    const showNonDashWO = this.shouldShowNonDashWO();

    const fourthFetch = this.shouldShowCsPending()
      ? dispatch(getCsPending())
      : dispatch(getFailedActions(hours));

    // Fetch all data and collect wo_memfis from responses
    Promise.all([
      dispatch(getIngests(hours)),
      dispatch(getTransfers(hours)),
      dispatch(getProxies(hours)),
      fourthFetch
    ]).then(responses => {
      // Collect all wo_memfis from the responses
      const woMemfisSet = new Set();

      responses.forEach(response => {
        if (response && Array.isArray(response)) {
          response.forEach(item => {
            if (item.wo_memfis || item.WOMemfis) {
              woMemfisSet.add(item.wo_memfis || item.WOMemfis);
            }
          });
        }
      });

      // Convert Set to Array and fetch work orders with the collected wo_memfis
      const woMemfisList = showNonDashWO ? Array.from(woMemfisSet) : [];
      dispatch(getWorkOrders(woMemfisList, hours));
    }).catch(error => {
      console.error('Error fetching data:', error);
      // Still try to fetch work orders with empty list on error
      dispatch(getWorkOrders([], hours));
    });

    this.setState({lastUpdated: new Date()});
  };

  getHealthStatus = (workOrders) => {
    if (!this.shouldShowCalculatedStatus()) {
      return {status: 'Under Construction', color: '#888888'};
    }
    // Calculate health status based on work orders with problematic actions
    const problematicWOs = workOrders.filter(wo => wo.has_problematic_action);
    if (problematicWOs.length === 0) {
      return {status: 'HEALTHY', color: '#28a745'};
    }

    // Find the worst urgency (minimum value) among problematic work orders
    const healthyStatus = problematicWOs.reduce((min, wo) => {
      const urgency = wo.urgency !== undefined && wo.urgency !== null ? wo.urgency : 3;
      return Math.min(min, urgency);
    }, 3);

    // Map urgency to status text and color
    switch (healthyStatus) {
      case 0:
        return {status: 'CRITICAL', color: '#dc3545'}; // Red - Overdue
      case 1:
        return {status: 'URGENT', color: '#fd7e14'}; // Orange - < 6h
      case 2:
        return {status: 'AT RISK', color: '#ffc107'}; // Yellow - < 24h
      case 3:
      default:
        return {status: 'HEALTHY', color: '#28a745'}; // Green - > 24h
    }
  };

  // Memoize the filtered+sorted work orders so its array reference stays stable across
  // renders triggered by unrelated slice updates (ingests/transfers/failed actions).
  getSortedWorkOrderData = (data, showComplete) => {
    const cache = this._woDataCache;
    if (cache.input === data && cache.showComplete === showComplete) {
      return cache.output;
    }
    let result = data || [];
    if (!showComplete) {
      result = result.filter(wo => wo.qc_status !== 'COMPLETE');
    }
    result = [...result].sort((a, b) => {
      const aComplete = a.qc_status === 'COMPLETE' ? 1 : 0;
      const bComplete = b.qc_status === 'COMPLETE' ? 1 : 0;
      if (aComplete !== bComplete) return aComplete - bComplete;
      const aDue = a.hours_until_due || 0;
      const bDue = b.hours_until_due || 0;
      return aDue - bDue;
    });
    cache.input = data;
    cache.showComplete = showComplete;
    cache.output = result;
    return result;
  };

  getWidgetConfigs = () => {
    const {statusDashboard} = this.props;
    const {ingests, transfers, proxies, failedActions, csPending, workOrders} = statusDashboard;

    const woData = this.getSortedWorkOrderData(workOrders.data, this.shouldShowCompleteWO());

    // Map data sources by key for efficient lookup
    const dataSources = {
      ingests: {data: ingests.data, loading: ingests.loading},
      transfers: {data: transfers.data, loading: transfers.loading},
      proxies: {data: proxies.data, loading: proxies.loading},
      failedActions: {data: failedActions.data, loading: failedActions.loading},
      csPending: {data: csPending.data, loading: csPending.loading},
      workOrders: {data: woData, loading: workOrders.loading}
    };

    // Combine dynamic metadata with data - columns reference static arrays
    return this.getWidgetMetadata().map(meta => ({
      ...meta,
      columns: WIDGET_COLUMNS[meta.key],
      data: dataSources[meta.key].data,
      loading: dataSources[meta.key].loading
    }));
  };

  handleMaximize = (widgetKey) => {
    // Snapshot all widgets' data at this moment
    const widgetConfigs = this.getWidgetConfigs();
    const allSnapshotData = {};
    widgetConfigs.forEach(config => {
      allSnapshotData[config.key] = [...config.data];
    });

    this.setState({
      maximizedWidgetKey: widgetKey,
      snapshotData: allSnapshotData
    });
  };

  handleCloseMaximized = () => {
    this.setState({
      maximizedWidgetKey: null,
      snapshotData: {}
    });
  };

  handleWidgetChange = (widgetKey) => {
    // Just change the active widget, data comes from snapshot
    this.setState({
      maximizedWidgetKey: widgetKey
    });
  };

  render() {
    const {statusDashboard} = this.props;
    const {workOrders} = statusDashboard;
    const {lastUpdated, maximizedWidgetKey, snapshotData} = this.state;
    const hours = this.getHoursParam() || 6; // 6 - default value on server API

    // Calculate health status from work orders
    const healthStatus = this.getHealthStatus(workOrders.data || []);

    // Get widget configurations
    const widgetConfigs = this.getWidgetConfigs();
    const showProxies = this.shouldDisplayProxies();
    const visibleWidgetConfigs = showProxies
      ? widgetConfigs
      : widgetConfigs.filter(config => config.key !== 'proxies');

    return (
      <TVLayout title="MAM Status Dashboard">
        <DashboardHeader>
          <HeaderTitle>MAM Status Dashboard</HeaderTitle>
          <Clock lastUpdated={lastUpdated} hours={hours} />
          <HeaderRight>
            <HealthBadge $color={healthStatus.color}>{healthStatus.status}</HealthBadge>
          </HeaderRight>
        </DashboardHeader>
        <DashboardGrid $showProxies={showProxies}>
          {visibleWidgetConfigs.map(config => (
            <Widget
              key={config.title}
              widgetKey={config.key}
              title={config.title}
              columns={config.columns}
              data={config.data}
              loading={config.loading}
              pathField={config.pathField}
              pathField2={config.pathField2}
              onMaximize={this.handleMaximize}
              fileMakerURL={workOrders.fileMakerURL}
              qcOnDemandBaseUrl={workOrders.qcOnDemandBaseUrl}
            />
          ))}
        </DashboardGrid>
        {maximizedWidgetKey && (
          <MaximizedPopup
            widgets={widgetConfigs}
            activeWidgetKey={maximizedWidgetKey}
            snapshotData={snapshotData}
            onWidgetChange={this.handleWidgetChange}
            onClose={this.handleCloseMaximized}
            fileMakerURL={workOrders.fileMakerURL}
            qcOnDemandBaseUrl={workOrders.qcOnDemandBaseUrl}
          />
        )}
      </TVLayout>
    );
  }
}

StatusDashboard.propTypes = {
  dispatch: PropTypes.func.isRequired,
  statusDashboard: PropTypes.object.isRequired,
};

function mapStateToProps(state) {
  return {
    statusDashboard: state.statusDashboard
  };
}

export default connect(mapStateToProps)(withRouter(StatusDashboard));
